import type { NotificationPayloadT } from 'lib-shared-legacy-types';
import map from 'lodash/map';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { notificationActions } from 'components/notifications/actions/notification';
import {
  readWidget,
  updateWidget,
  validateWidgetName,
} from 'components/settings/actions/chatWidgets';
import { hasFeature } from 'components/shared/featureFlag';
import type { NodeDataT } from 'fable/flowEditor';
import { withRouter } from 'hocs/router';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { chatFlowActions } from '../actions/chatFlow';
import type { FlowChatActionT } from '../api/types';
import PowerChatFlow, { FlowChatOwnPropsT } from '../components/PowerChatFlow';

export type FlowDataT = NodeDataT<FlowChatActionT[]>;

type UpdateWigetT = {
  id: string;
  name: string;
};

const { notificationAdd } = notificationActions;
const { chatFlowAdd, chatFlowClearState, chatFlowEdit, chatFlowGet } =
  chatFlowActions;

const getId = (
  _state: RootReducerStateT,
  ownProps: FlowChatOwnPropsT,
): string | undefined => ownProps.params.flowId;
const getNodes = (
  state: RootReducerStateT,
): Record<string, FlowChatActionT> | null | undefined => state.chatNodes.nodes;
const getName = (state: RootReducerStateT): string => state.chatFlow.name;
const selectFlowData = createSelector(
  [getId, getNodes, getName],
  (
    id: string | undefined,
    nodes: Record<string, FlowChatActionT> | null | undefined,
    name: string,
  ): FlowDataT => {
    return {
      actions: map(nodes, (n) => n),
      active: true,
      created_at: '',
      id: id ?? '',
      name,
      updated_at: '',
    };
  },
);

const mapStateToProps = (
  state: RootReducerStateT,
  ownProps: FlowChatOwnPropsT,
) => {
  return {
    chatWidget: state.settings.chatWidget,
    flowData: selectFlowData(state, ownProps),
    hasTagNode: true,
    isCanvasEnabled: hasFeature(state, 'routing-use-canvas'),
    id: getId(state, ownProps),
    name: state.chatFlow.name,
    status: state.chatFlow.meta.asyncStatus,
    widget_id: state.chatFlow.widget_id,
    agentDisplayName: state.settings.profile.profile.name,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addFlow: bindActionToPromise(dispatch, chatFlowAdd),
    fetchFlow: bindActionToPromise(dispatch, chatFlowGet),
    chatFlowClearState: () => dispatch(chatFlowClearState()),

    getWidget: (widgetId: string) => dispatch(readWidget(widgetId)),
    notificationAdd: (payload: NotificationPayloadT) =>
      dispatch(notificationAdd(payload)),

    updateFlow: bindActionToPromise(dispatch, chatFlowEdit),
    updateWidget: (widget: UpdateWigetT) =>
      dispatch(updateWidget(widget as any)),

    validateWidgetName: (name?: null | string) =>
      dispatch(validateWidgetName(name as any)),
  };
};

export type FlowChatStatePropsT = ReturnType<typeof mapStateToProps>;
export type FlowChatDispatchPropsT = ReturnType<typeof mapDispatchToProps>;

export default withRouter(
  connect<
    FlowChatStatePropsT,
    FlowChatDispatchPropsT,
    FlowChatOwnPropsT,
    RootReducerStateT
  >(
    mapStateToProps,
    mapDispatchToProps,
  )(PowerChatFlow),
);
